/* eslint-disable import/prefer-default-export */
/**
 * Purchase Selectors
 * Memoized selectors for accessing purchase state data
 */

/**
 * Select entire purchase state as plain JavaScript object
 *
 * @param {Object} state - Redux state
 * @returns {Object} Purchase data as plain object
 */
export const selectPurchase = (state) => {
  return state.purchase?.toJS() || {};
};

/**
 * Select purchase token from purchase state
 *
 * @param {Object} state - Redux state
 * @returns {string} Purchase token
 */
export const selectPurchaseToken = (state) => {
  return state.purchase?.get('token') || '';
};

/**
 * Select passenger ancillaries by passenger ID and segment
 * Returns ancillaries array for a specific passenger and trip segment
 *
 * @param {number|string} passengerId - ID of the passenger
 * @param {string} segment - Segment: 'outgoing' or 'incoming'
 * @returns {Function} Selector function
 */
export const selectPassengerAncillaries = (passengerId, segment = 'outgoing') => {
  return (state) => {
    const passengers = state.purchase?.get('passengers');

    if (!passengers || passengers.size === 0) {
      return [];
    }

    let passengerAncillaries = [];
    passengers.forEach((passenger) => {
      if (passenger.get('id') === passengerId) {
        const key = `${segment}Ancillaries`;
        passengerAncillaries = passenger.get(key)?.toJS() || [];
      }
    });

    return passengerAncillaries;
  };
};

/**
 * Get vivaPnr from purchase meta.
 * This is the PNR stored in purchase.meta.vivaPnr when purchase is received.
 * Use this selector to check if Viaja Ahorrando is active for the current purchase.
 * @param {Object} state - Redux state
 * @returns {string|null} vivaPnr from purchase meta or null
 */
export const getVivaPnrFromPurchaseMeta = (state) => {
  const { purchase } = state;
  if (!purchase) return null;

  // Handle both Immutable Map and plain JS object
  const meta = purchase.get ? purchase.get('meta') : purchase.meta;
  if (!meta) return null;

  // Handle both Immutable Map and plain JS object
  const vivaPnr = meta.get ? meta.get('vivaPnr') : meta.vivaPnr;
  return vivaPnr || null;
};

/**
 * Check if Viaja Ahorrando is active based on purchase meta.
 * Returns true if vivaPnr is present in purchase.meta.vivaPnr.
 * @param {Object} state - Redux state
 * @returns {boolean} True if Viaja Ahorrando is active
 */
export const isViajaAhorrandoActiveInPurchase = (state) => {
  const vivaPnr = getVivaPnrFromPurchaseMeta(state);
  return Boolean(vivaPnr);
};
